import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui";

// Solo PDF e immagini hanno un'anteprima in pagina; gli altri file restano link
const MIME_PER_ESTENSIONE = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
};

function estensione(nome) {
  const m = /\.([a-z0-9]+)$/i.exec(nome || "");
  return m ? m[1].toLowerCase() : "";
}

function nomeDaUrl(url) {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
  } catch {
    return "";
  }
}

/**
 * Elenco dei file con anteprima disponibile. I documenti multi-file hanno
 * l'elenco in `allegati`; quelli caricati prima del 2026-07-03 hanno solo
 * `urlSharepoint` e il contenuto arriva da /documenti/:id/file.
 */
function fileConAnteprima(documenti) {
  const files = [];
  for (const doc of documenti) {
    if (doc.allegati?.length) {
      for (const a of doc.allegati) {
        const mime = MIME_PER_ESTENSIONE[estensione(a.name)];
        if (!mime) continue;
        files.push({
          key: `${doc.id}:${a.id}`,
          nome: doc.allegati.length > 1 ? `${doc.nome} — ${a.name}` : doc.nome,
          mime,
          src: `/api/documenti/${doc.id}/allegati/${a.id}/download`,
          webUrl: a.webUrl || doc.urlSharepoint,
        });
      }
    } else if (doc.urlSharepoint) {
      const mime =
        MIME_PER_ESTENSIONE[estensione(nomeDaUrl(doc.urlSharepoint))];
      if (!mime) continue;
      files.push({
        key: doc.id,
        nome: doc.nome,
        mime,
        src: `/api/documenti/${doc.id}/file`,
        webUrl: doc.urlSharepoint,
      });
    }
  }
  return files;
}

function AnteprimaDocumenti({ documenti }) {
  const { getAccessToken } = useAuth();
  const files = useMemo(() => fileConAnteprima(documenti || []), [documenti]);
  const [selezionato, setSelezionato] = useState(null);
  const [urls, setUrls] = useState({});
  const [errori, setErrori] = useState({});
  const urlsRef = useRef({});

  const corrente = files.find((f) => f.key === selezionato) || files[0] || null;

  // Il file si scarica solo quando viene selezionato (i rapportini possono essere pesanti)
  useEffect(() => {
    if (!corrente || urlsRef.current[corrente.key]) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(corrente.src, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // Il MIME salvato può essere generico (es. octet-stream dagli allegati email):
        // senza il tipo giusto il browser non mostra il PDF nell'iframe
        const blob = new Blob([await res.blob()], { type: corrente.mime });
        const url = URL.createObjectURL(blob);
        urlsRef.current[corrente.key] = url;
        if (!cancelled) setUrls((prev) => ({ ...prev, [corrente.key]: url }));
      } catch {
        if (!cancelled)
          setErrori((prev) => ({ ...prev, [corrente.key]: true }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [corrente, getAccessToken]);

  useEffect(() => {
    return () => {
      Object.values(urlsRef.current).forEach(URL.revokeObjectURL);
    };
  }, []);

  if (!corrente) return null;

  const url = urls[corrente.key];

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-medium text-gray-500">
          Anteprima documenti
        </h3>
        {corrente.webUrl && (
          <a
            href={corrente.webUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:underline flex-shrink-0"
          >
            Apri in una nuova finestra
          </a>
        )}
      </div>

      {files.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {files.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setSelezionato(f.key)}
              className={`text-xs px-2.5 py-1 rounded-full border max-w-full truncate transition-colors ${
                f.key === corrente.key
                  ? "bg-primary-600 border-primary-600 text-white"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title={f.nome}
            >
              {f.nome}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
        {errori[corrente.key] ? (
          <p className="text-sm text-gray-500 text-center py-10">
            Anteprima non disponibile
          </p>
        ) : !url ? (
          <p className="text-sm text-gray-500 text-center py-10">
            Caricamento anteprima...
          </p>
        ) : corrente.mime === "application/pdf" ? (
          <iframe
            src={url}
            title={corrente.nome}
            className="w-full h-[75vh] bg-white"
          />
        ) : (
          <img
            src={url}
            alt={corrente.nome}
            className="w-full max-h-[75vh] object-contain"
          />
        )}
      </div>
    </Card>
  );
}

export default AnteprimaDocumenti;
